import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import FormatListNumberedSharpIcon from '@mui/icons-material/FormatListNumberedSharp';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from "@mui/material/styles";
import React from "react";
import Clock from "../clock/clock";
import { useEtBreakpoints, useEtExtras } from "../responsive/useEtView";
import "./header.css";
import { iconStyle } from "./headerConstants";


// How each sortable column reads in each direction — used to tell the user, on
// hover, exactly what order a click will produce (and it flips as they toggle).
const SORT_HINT = {
    sentence: { asc: 'A → Z (alphabetical)', desc: 'Z → A (reverse)' },
    sec:      { asc: 'shortest first',       desc: 'longest first' },
    length:   { asc: 'fewest characters',    desc: 'most characters' },
};

const Header = ({ header, onClick, sortOrder = null, checked = [false, false], handleCheck, filterStore, userStore, currentView, precision, showExtraMobileColumns = false, activeFilterFields = null }) => {
    const theme = useTheme();
    const mode = theme.palette.mode;

    // Effective breakpoints honor the user's ET view toggle (auto / phone / full)
    const { isXSmallScreen, isSmallScreen, isMediumScreen } = useEtBreakpoints();
    const etExtras = useEtExtras();

    // XS base: checkbox + thumbnail + full-width sentence + timecode past the right edge
    // (swipe the transcript left and the ± trim steppers roll in).
    // Extra (toggled via showExtraMobileColumns): sec, length, question
    const shouldShowColumn = (field) => {
        // Keep in sync with ItemContent.jsx shouldShowColumn exactly.
        const xsBase = etExtras.timecode ? ['', 'play', 'sentence'] : ['', 'play', 'sentence', 'duration'];
        const xsExtra = ['sec', 'length', 'question'];
        const smVisible = ['', 'order', 'play', 'sentence', 'speaker', 'sentiment', 'sec', 'length', 'question', 'duration'];
        if (isXSmallScreen) return xsBase.includes(field) || (showExtraMobileColumns && xsExtra.includes(field));
        if (isSmallScreen)  return smVisible.includes(field) || showExtraMobileColumns;
        return true;
    };

    const bg = theme.palette.background.paper;
    const getStickyHeaderStyle = (obj) => {
        if (!isXSmallScreen) return {};
        // XS pinning (keep in sync with ItemContent.jsx getStickyStyle): checkbox(26px) +
        // play(146px) sticky-left; sentence + duration scroll (duration rolls in from the right).
        if (obj.type === 'checkbox')  return { position: 'sticky', left: 0,      zIndex: 12, backgroundColor: bg };
        if (obj.type === 'video')     return { position: 'sticky', left: '26px', zIndex: 12, backgroundColor: bg };
        return {};
    };

    // Styling for header cells based on screen size
    const getResponsiveHeaderStyle = (obj) => {
        let width = obj.width;

        // Use dynamic width for video columns if filterStore and currentView are available
        if (obj.type === 'video' && filterStore && currentView) {
            const dynamicVideoWidth = filterStore.getVideoWidth(currentView);
            if (isXSmallScreen) {
                width = '146px'; // must match the body's xs video td (ItemContent getResponsiveStyles)
            } else if (isSmallScreen) {
                width = '100px';
            } else if (isMediumScreen) {
                width = '150px';
            } else {
                width = `${dynamicVideoWidth}px`;
            }
        }

        // Use dynamic width for sentence columns to match body
        if (obj.field === 'sentence' && filterStore && currentView) {
            const dynamicVideoWidth = filterStore.getVideoWidth(currentView);

            if (isXSmallScreen) {
                width = 'calc(100vw - 180px)'; // match the body's xs transcript width (rolling-column geometry)
            } else if (isSmallScreen) {
                width = dynamicVideoWidth <= 50 ? '200px' : '150px';
            } else if (isMediumScreen) {
                width = dynamicVideoWidth <= 50 ? '350px' :
                    dynamicVideoWidth <= 100 ? '300px' : '250px';
            } else {
                // Same calculation as in ItemContent:
                // 30px video → 300px sentence
                // 90px video → 280px sentence  
                // 150px video → 250px sentence
                let sentenceWidth;
                if (dynamicVideoWidth <= 30) {
                    sentenceWidth = 300;
                } else if (dynamicVideoWidth <= 90) {
                    // Linear interpolation between 30px→300px and 90px→280px
                    const ratio = (dynamicVideoWidth - 30) / (90 - 30);
                    sentenceWidth = 300 + (280 - 300) * ratio;
                } else {
                    // Linear interpolation between 90px→280px and 150px→250px
                    const ratio = (dynamicVideoWidth - 90) / (150 - 90);
                    sentenceWidth = 280 + (250 - 280) * ratio;
                }
                width = `${Math.round(sentenceWidth)}px`;
            }
        }

        const baseStyle = {
            width: width,
            textAlign: obj.align || 'center',
            verticalAlign: 'middle',
            fontSize: isXSmallScreen ? '12px' : isSmallScreen ? '13px' : '14px',
            padding: isXSmallScreen ? '2px' : isSmallScreen ? '3px' : '4px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            // Phone: paint every header cell the theme's paper color — the thead's own css
            // background (--header-bg-color, near-black) otherwise shows through non-sticky
            // cells and reads as a mismatched black patch next to the pinned grey ones.
            ...(isXSmallScreen ? { backgroundColor: bg } : {}),
        };

        // Phone: pin the checkbox header to 26px to match the body cells and the video
        // column's sticky left offset (ItemContent.jsx getStickyStyle).
        if (obj.type === 'checkbox' && isXSmallScreen) {
            return { ...baseStyle, width: '26px', minWidth: '26px', maxWidth: '26px', padding: '2px 0' };
        }

        // Special override for Clock component column
        if (obj.type === 'icon' && obj.field === 'duration') {
            return {
                ...baseStyle,
                padding: 0, // Remove padding from TH
                textAlign: 'center', // Center align TH content
                width: '80px', // Fixed width to prevent resizing when precision changes
                minWidth: '80px', // Ensure minimum width
                maxWidth: '80px', // Prevent expansion
            };
        }
        return baseStyle;
    };

    // maybe a string or a component. `active` = this column is the current sort,
    // so the carrot goes amber to match its order label below.
    const SortArrow = ({ index, active }) => {
        const sx = { fontSize: isSmallScreen ? 16 : 20, color: active ? '#f59e0b' : 'inherit' };
        return header[index].isUp ? <ArrowDropDownIcon sx={sx} /> : <ArrowDropUpIcon sx={sx} />;
    };

    return (
        <tr
            className="responsive-header"
            style={{
                width: '100%',
                top: 0,
                backgroundColor: mode === 'dark' ? 'black' : 'white',
                height: isXSmallScreen ? '28px' : isSmallScreen ? '32px' : '60px',
            }}
        >
            {header.map((obj, index) => {
                // Only show columns that are visible and should be shown on current screen size
                if (obj.visible && shouldShowColumn(obj.field)) {
                    if (obj.restore)
                        return (
                            <th key={index} style={getResponsiveHeaderStyle(obj)}>
                                <Tooltip title="Pin selected clips to top in pick order" placement="right">
                                    <IconButton
                                        onClick={() => onClick(index)}
                                        style={{
                                            padding: isXSmallScreen ? '2px' : '4px',
                                            marginRight: isXSmallScreen ? '0' : '2px'
                                        }}
                                    >
                                        <FormatListNumberedSharpIcon sx={{
                                            ...iconStyle,
                                            fontSize: isXSmallScreen ? 12 : isSmallScreen ? 14 : 15
                                        }} />
                                    </IconButton>
                                </Tooltip>
                            </th>
                        )
                    if (obj.type === 'checkbox')
                        return (
                            <th key={index} style={{ ...getResponsiveHeaderStyle(obj), ...getStickyHeaderStyle(obj) }}>
                                <Tooltip title={checked[0] ? 'Deselect all clips' : 'Select all clips'} placement="right">
                                    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Checkbox
                                            sx={{
                                                color: '#E53935',
                                                '&.Mui-checked': { color: '#E53935' },
                                                '&.MuiCheckbox-indeterminate': { color: '#E53935' },
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: isXSmallScreen ? 12 : isSmallScreen ? 14 : 15
                                                },
                                                padding: '2px'
                                            }}
                                            checked={checked[0]}
                                            indeterminate={checked[1]}
                                            onChange={handleCheck}
                                        />
                                        <Box component="span" onClick={handleCheck}
                                            sx={{ fontSize: '8px', fontWeight: 700, letterSpacing: '0.5px', lineHeight: 1,
                                                color: checked[0] || checked[1] ? '#E53935' : 'rgba(255,255,255,0.5)',
                                                cursor: 'pointer', userSelect: 'none', mt: '-1px' }}>
                                            ALL
                                        </Box>
                                    </Box>
                                </Tooltip>
                            </th>
                        )
                    if (obj.field === 'duration') {
                        // This cell shows the LIST / PICK totals, and it is ALSO the Time column's
                        // header — so it must sort like every other heading (Isaac: "all the other
                        // headers sort so that one should too"). It returns early, before the
                        // generic sortable path below, so the click + arrow are wired here.
                        // The sort field is start_in (see getPrecisionField), i.e. chronological —
                        // which in an Archive interleaves the videos instead of keeping them in
                        // blocks. Same ordering the toolbar's Chrono button applies.
                        const sortActive = sortOrder && sortOrder.field === 'duration';
                        return (
                            <th key={index} style={{ ...getResponsiveHeaderStyle(obj), ...getStickyHeaderStyle(obj) }}>
                                <Tooltip title="Sort by timecode — earliest first, then latest. In an Archive this mixes the videos together in time order." placement="bottom" arrow>
                                    <Box
                                        onClick={() => onClick(index, (header[index].isUp ? 'down' : 'up'))}
                                        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                              borderRadius: '6px', '&:hover': { backgroundColor: 'rgba(255,255,255,0.07)' } }}
                                    >
                                        <Clock header={true} showLabels={true} duration={[userStore.getTotalDuration(), userStore.getDuration()]} />
                                        <SortArrow index={index} active={!!sortActive} />
                                    </Box>
                                </Tooltip>
                            </th>
                        )
                    }
                    // Phone only: head the thumbnail column "Clips" with a tiny legend, since the
                    // letter (speaker) and the +/=/− marks (emotion) on each clip aren't obvious.
                    if (isXSmallScreen && obj.type === 'video') {
                        // Dynamic legend: the ACTUAL detected speaker letters, then the emotion marks
                        // on a separate line. When a Speaker/Emotion filter is active, the picked ones
                        // stay bright and the rest dim — mirroring the "Filter clips by" toolbar.
                        const spk = (filterStore && filterStore.getSpeakers && filterStore.getSpeakers(currentView)) || {};
                        const spkKeys = Object.keys(spk).sort();
                        const activeSpk = spkKeys.filter((k) => spk[k]);
                        // Compact range "A–H" by default; the picked letters when a Speaker filter is on.
                        const speakerLabel = activeSpk.length
                            ? activeSpk.join(' ')
                            : (spkKeys.length > 1 ? `${spkKeys[0]}–${spkKeys[spkKeys.length - 1]}` : (spkKeys[0] || 'A–H'));
                        const sent = (filterStore && filterStore.getSentiments && filterStore.getSentiments(currentView)) || {};
                        const anySent = !!(sent.positive || sent.neutral || sent.negative);
                        const emo = [
                            { s: '+', c: '#22c55e', on: !!sent.positive },
                            { s: '=', c: '#8a93a6', on: !!sent.neutral },
                            { s: '−', c: '#ff5a5a', on: !!sent.negative },
                        ];
                        const emoColor = (e) => (anySent && !e.on ? e.c + '66' : e.c);
                        return (
                            <th key={index} style={{ ...getResponsiveHeaderStyle(obj), ...getStickyHeaderStyle(obj) }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2, py: '2px' }}>
                                    <Box component="span" sx={{ fontSize: '13px', fontWeight: 800, color: '#f3f4f9' }}>Clips</Box>
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', fontSize: '8px', lineHeight: 1, color: '#8d94a8', whiteSpace: 'nowrap', mt: '5px' }}>
                                        <Box component="span" sx={{ color: '#6aa8ff', fontWeight: 700, mr: '2px' }}>{speakerLabel}</Box>
                                        speakers
                                        <Box component="span" sx={{ mx: '5px', opacity: 0.4 }}>·</Box>
                                        {emo.map((e) => (
                                            <Box key={e.s} component="span" sx={{ color: emoColor(e), fontWeight: 700, mr: '2px' }}>{e.s}</Box>
                                        ))}
                                        emotion
                                    </Box>
                                </Box>
                            </th>
                        )
                    }
                    // Color-code filter-linked columns so users visually connect them to their toolbar filters
                    const fieldColor = null;

                    // Determine if this is a long title that needs special handling.
                    // The transcript column is exempt: it's the widest column on every screen,
                    // and the xs .long-title clamp (max-width 30px) was truncating it to "Tra".
                    const isLongTitle = typeof obj.title === 'string' && obj.title.length > 5 && obj.field !== 'sentence';
                    const isActiveSort = sortOrder && sortOrder.column === obj.field && sortOrder.label;

                    // This column is actively narrowing the list — say so, so it is obvious
                    // WHY you are seeing fewer clips and where to go to undo it.
                    const isFiltering = !!(activeFilterFields && activeFilterFields.has && activeFilterFields.has(obj.field));

                    return (
                        <th
                            key={index}
                            title={isFiltering ? 'This column is filtering the list' : undefined}
                            style={{
                                ...getResponsiveHeaderStyle(obj),
                                ...getStickyHeaderStyle(obj),
                                ...(fieldColor ? { color: fieldColor } : {}),
                                ...(isFiltering ? {
                                    color: '#ff7a9c',
                                    boxShadow: 'inset 0 -2px 0 #e0466b',
                                } : {}),
                            }}
                            className={isLongTitle ? 'long-title' : ''}
                        >
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: obj.align === 'right' ? 'flex-end' : obj.align === 'center' ? 'center' : 'flex-start',
                                justifyContent: 'center',
                                width: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                                <Box sx={{
                                    display: 'inline-flex',
                                    alignItems: obj.align === 'right' ? 'flex-end' : obj.align === 'center' ? 'center' : 'flex-start',
                                    justifyContent: obj.align === 'right' ? 'flex-end' : obj.align === 'center' ? 'center' : 'flex-start',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {obj.sort &&
                                        <Tooltip
                                            title={(() => {
                                                const hint = SORT_HINT[obj.field] || { asc: 'ascending', desc: 'descending' };
                                                // Next click flips direction: isUp now → 'down' (desc) next; else 'up' (asc).
                                                const nextAsc = !header[index].isUp;
                                                const name = typeof obj.title === 'string' ? obj.title : 'this column';
                                                return `Sort by ${name}: ${nextAsc ? hint.asc : hint.desc}`;
                                            })()}
                                            placement="bottom"
                                            enterDelay={400}
                                        >
                                        <IconButton
                                            onClick={() => onClick(index, (header[index].isUp ? 'down' : 'up'))}
                                            disableRipple
                                            sx={{
                                                padding: 0,
                                                ml: isXSmallScreen ? 0 : 0.2,
                                                fontSize: isXSmallScreen ? 13 : isSmallScreen ? 14 : 18,
                                                fontWeight: isXSmallScreen ? 800 : 'inherit', // match the fat "Clips" header on phones
                                                color: fieldColor || 'inherit',
                                                cursor: 'pointer',
                                                // Carrot is dim at rest, full on hover (reveals that the
                                                // column is sortable), and stays full when it's the active sort.
                                                '& .MuiSvgIcon-root': {
                                                    opacity: isActiveSort ? 1 : 0.4,
                                                    transition: 'opacity 0.15s ease',
                                                },
                                                '&:hover .MuiSvgIcon-root': { opacity: 1 },
                                            }}
                                        >
                                            {typeof obj.title === 'string' && isXSmallScreen && obj.field !== 'sentence'
                                                ? obj.title.substring(0, 3)
                                                : obj.title
                                            }
                                            <SortArrow index={index} active={isActiveSort} />
                                        </IconButton>
                                        </Tooltip>
                                    }
                                    {!obj.sort && (
                                        typeof obj.title === 'string' && isXSmallScreen && obj.field !== 'sentence'
                                            ? obj.title.substring(0, 3)
                                            : obj.title
                                    )}
                                </Box>
                                {(() => {
                                    // Keep the static descriptor ("by sentences") AND, when this column
                                    // drives the active sort, stack an amber order indicator below it
                                    // ("in order", "long→short") so you see both what the column is and
                                    // how the clips are currently ordered. Stacking avoids cutting off
                                    // narrow columns like Seconds.
                                    const isActiveSort = sortOrder && sortOrder.column === obj.field && sortOrder.label;
                                    if (!obj.subtitle && !isActiveSort) return null;
                                    const lineSx = {
                                        fontSize: isXSmallScreen ? '8px' : isSmallScreen ? '9px' : '10px',
                                        lineHeight: 1,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: '100%',
                                        textAlign: 'left',
                                    };
                                    return (
                                        <Box sx={{ marginTop: '5px' }}>
                                            {obj.subtitle && (
                                                <Box sx={{ ...lineSx, color: 'text.secondary' }}>
                                                    {typeof obj.subtitle === 'string' && isXSmallScreen && obj.field !== 'sentence'
                                                        ? obj.subtitle.substring(0, 3)
                                                        : obj.subtitle}
                                                </Box>
                                            )}
                                            {isActiveSort && (
                                                <Box sx={{ ...lineSx, color: '#f59e0b', fontWeight: 700, marginTop: obj.subtitle ? '2px' : 0 }}>
                                                    {sortOrder.label}
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })()}
                            </Box>
                        </th>
                    )
                }
                return null
            })}
        </tr>
    )
}

export default Header;